import React, { useState } from 'react';
import { X, MapPin, ArrowLeft } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { emplacementsData } from '../data/emplacements';

const PanneauxModal = ({ isOpen, onClose }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);

  const categories = [
    {
      id: 'deroulants',
      name: 'Panneaux Déroulants',
      description: '10 emplacements stratégiques à Bangui',
      emplacements: emplacementsData.deroulants,
      image: 'https://customer-assets.emergentagent.com/job_crm-showcase-5/artifacts/zrgcjxfz_11062FAD-C8BF-41B2-9E19-490FD5854A6C.png',
      color: 'from-purple-500 to-pink-600',
      badgeColor: 'bg-purple-100 text-purple-700'
    },
    {
      id: 'led',
      name: 'Panneaux LED',
      description: '3 emplacements premium',
      emplacements: emplacementsData.led,
      image: 'https://customer-assets.emergentagent.com/job_crm-showcase-5/artifacts/x2rcxn1r_D59C4209-92E8-4E07-A19E-ACE030AC42A4.png',
      color: 'from-blue-500 to-cyan-600',
      badgeColor: 'bg-blue-100 text-blue-700'
    },
    {
      id: 'grandFormat',
      name: 'Panneau Grand Format',
      description: '1 emplacement exceptionnel',
      emplacements: emplacementsData.grandFormat,
      image: 'https://customer-assets.emergentagent.com/job_crm-showcase-5/artifacts/xrkhnmmg_IMG_3008.jpeg',
      color: 'from-orange-500 to-red-600',
      badgeColor: 'bg-orange-100 text-orange-700'
    },
    {
      id: 'panneauxPoteaux',
      name: 'Panneaux Poteaux',
      description: '4 emplacements sur les avenues principales',
      emplacements: emplacementsData.panneauxPoteaux,
      image: 'https://customer-assets-cm19k8pv.emergentagent.net/job_crm-showcase-5/artifacts/p5pwmf2g_A58D5933-21B0-4ED5-8E31-8A8E79B49652.png',
      color: 'from-green-500 to-teal-600',
      badgeColor: 'bg-green-100 text-green-700'
    }
  ];

  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId);
  };

  const handleBack = () => {
    setSelectedCategory(null);
  };

  const handleClose = () => {
    setSelectedCategory(null);
    onClose();
  };

  const selectedCategoryData = categories.find(cat => cat.id === selectedCategory);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            {selectedCategory && (
              <button
                onClick={handleBack}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors mr-4"
                aria-label="Retour"
              >
                <ArrowLeft size={24} />
              </button>
            )}
            <DialogTitle className="text-3xl font-bold flex-1">
              {selectedCategory ? selectedCategoryData.name : 'Nos Panneaux et Emplacements'}
            </DialogTitle>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Fermer"
            >
              <X size={24} />
            </button>
          </div>
          {!selectedCategory && (
            <p className="text-gray-600 mt-2">
              Découvrez nos différents types de panneaux publicitaires et leurs emplacements à Bangui
            </p>
          )}
        </DialogHeader>

        <div className="mt-6">
          {!selectedCategory ? (
            // Vue des catégories
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => handleCategoryClick(category.id)}
                  className="group relative overflow-hidden rounded-2xl border-2 border-gray-200 hover:border-primary transition-all hover:shadow-2xl hover:-translate-y-2 bg-white"
                >
                  {/* Image */}
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={category.image}
                      alt={category.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>

                  {/* Gradient overlay */}
                  <div className={`absolute inset-0 bg-gradient-to-t ${category.color} opacity-0 group-hover:opacity-20 transition-opacity`}></div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                      {category.name}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                      {category.description}
                    </p>
                    <Badge className={`${category.badgeColor} font-semibold`}>
                      {category.emplacements.length} emplacement{category.emplacements.length > 1 ? 's' : ''}
                    </Badge>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            // Vue de la liste des emplacements
            <div>
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 mb-6 border-2 border-gray-200">
                <p className="text-gray-700 text-lg">
                  {selectedCategoryData.description}
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {selectedCategoryData.emplacements.map((emplacement) => (
                  <div
                    key={emplacement.id}
                    className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-primary hover:shadow-lg transition-all"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className={`w-12 h-12 rounded-full ${selectedCategoryData.badgeColor} flex items-center justify-center font-bold text-lg`}>
                          {emplacement.id}
                        </div>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900 text-lg mb-2">
                          {emplacement.name}
                        </h4>
                        <div className="flex items-center gap-2 text-gray-600">
                          <MapPin size={16} />
                          <span className="text-sm">Bangui, RCA</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Info box */}
              <div className="mt-8 bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-blue-900 text-lg mb-2">
                  💡 Intéressé par ces emplacements ?
                </h4>
                <p className="text-blue-800 mb-4">
                  Utilisez le bouton "Demander un devis" pour obtenir une offre personnalisée incluant ces emplacements.
                </p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PanneauxModal;
